import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authStorage, loginApi, logoutApi, validateTokenApi } from '../utils/auth';

const AuthContext = createContext(null);

/**
 * AuthProvider wraps the entire app.
 * On mount it reads localStorage, validates the token against the server,
 * and decides whether to show the Login screen or the dashboard.
 */
export function AuthProvider({ children }) {
  // 'loading' | 'authenticated' | 'unauthenticated'
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState('');

  // ── On mount / reload: validate stored token ──────────────────
  useEffect(() => {
    const stored = authStorage.getToken();
    if (!stored) {
      setStatus('unauthenticated');
      return;
    }
    validateTokenApi(stored)
      .then((res) => {
        if (res.responseStatus) {
          setToken(stored);
          setUser(res.responseObject || authStorage.getUser());
          setStatus('authenticated');
        } else {
          authStorage.clear();
          setStatus('unauthenticated');
        }
      })
      .catch(() => {
        authStorage.clear();
        setStatus('unauthenticated');
      });
  }, []);

  // ── Login ─────────────────────────────────────────────────────
  const login = useCallback(async (username, password) => {
    setError('');
    try {
      const res = await loginApi({ username, password });
      // console.log("response : {} ", res);
      if (res.responseStatus && res.responseObject?.accessToken) {
        authStorage.setToken(res.responseObject?.accessToken);
        authStorage.setUser(res.responseObject);
        setToken(res.responseObject?.accessToken);
        setUser(res.responseObject);
        setStatus('authenticated');
        return true;
      }
      setError(res.responseMessage || 'Invalid credentials');
      return false;
    } catch {
      setError('Network error. Please try again.');
      return false;
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    const t = authStorage.getToken();
    if (t) await logoutApi(t).catch(() => { });
    authStorage.clear();
    setToken(null);
    setUser(null);
    setError('');
    setStatus('unauthenticated');
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, token, error, login, logout, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
